import classNames from 'classnames';
import React from 'react';

import {
  exportAuditLogs,
  exportPlateHistoryReport,
  exportSampleContainerHistory,
  exportLocationHistoryReport,
} from 'audit/AuditActions';
import {
  EXPORT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
  EXPORT_PLATE_HISTORY_DISPLAY_STRING,
  EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
  EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING,
} from 'audit/AuditConstants';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import {ResultsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';

export interface StorableExportButtonProps {
  storableId: string;
  buttonClassName?: string;
  buttonStyle?: ButtonStyle;
}

/** A single storable export, independent of whether it renders as a dropdown option or a menu item. */
export interface StorableExportOption {
  key: string;
  label: string;
  onSelect: () => void;
}

function getExportOptionsForEnterprise(storableId: string): Array<StorableExportOption> {
  const options: Array<StorableExportOption> = [];

  if (APIUtil.isContainer(storableId)) {
    options.push({
      key: 'exportContainerHistoryReport',
      label: EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
      onSelect: () => {
        exportSampleContainerHistory([storableId]);
      },
    });
  } else if (APIUtil.isPlate(storableId)) {
    options.push({
      key: 'exportPlateHistoryReport',
      label: EXPORT_PLATE_HISTORY_DISPLAY_STRING,
      onSelect: () => {
        exportPlateHistoryReport([storableId]);
      },
    });
  } else if (APIUtil.isLocation(storableId)) {
    options.push({
      key: 'exportLocationHistoryReport',
      label: EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
      onSelect: () => {
        exportLocationHistoryReport([storableId]);
      },
    });
  }

  // All storable types can export audit logs
  options.push({
    key: 'exportAuditLog',
    label: EXPORT_AUDIT_LOG_DISPLAY_STRING,
    onSelect: () => {
      exportAuditLogs([storableId]);
    },
  });

  const canExportResultAuditLogs =
    isFeatureEnabled({feature: ResultsFeature.RESULTS_SHOW_DETAILS, item: {id: storableId}}) &&
    !APIUtil.isLocation(storableId);
  if (canExportResultAuditLogs) {
    options.push({
      key: 'exportResultsAuditLog',
      label: EXPORT_RESULT_AUDIT_LOG_DISPLAY_STRING,
      onSelect: () => {
        exportAuditLogs([], {relatedResultsModelId: storableId});
      },
    });
  }

  return options;
}

/**
 * Every export available for a storable. Enterprise plans get the storable-specific reports; other plans
 * can only export the audit log, which `StorableExportButton` presents as a standalone button.
 */
export function getStorableExportOptions(storableId: string): Array<StorableExportOption> {
  if (licenses.hasEnterprisePlan()) {
    return getExportOptionsForEnterprise(storableId);
  }
  return [
    {
      key: 'exportAuditLog',
      label: EXPORT_AUDIT_LOG_DISPLAY_STRING,
      onSelect: () => {
        exportAuditLogs([storableId], {});
      },
    },
  ];
}

export default function StorableExportButton({
  storableId,
  buttonClassName,
  buttonStyle,
}: StorableExportButtonProps): JSX.Element {
  // This is shown in the storable's metadata tab and a grid view's sidebar
  const auditExportButtonStyle = buttonStyle || ButtonStyle.TERTIARY_BLUE;
  const options = licenses.hasEnterprisePlan() ? getExportOptionsForEnterprise(storableId) : [];
  const showDropdown = options.length > 1;

  return (
    <>
      {showDropdown ? (
        <Dropdown
          tooltip="Export report"
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FIT_CONTENT} dropLeft={true}>
              {options.map((option) => (
                <Option
                  onSelect={option.onSelect}
                  dataTestAttrs={{component: 'StorableExport', element: 'dropdownOption', key: option.key}}
                  key={option.key}
                >
                  {option.label}
                </Option>
              ))}
            </Menu>
          }
        >
          <Button
            icon="share-v2"
            isDropdown={true}
            buttonStyle={auditExportButtonStyle}
            size={ButtonSize.SMALL}
            className={classNames('u-no-wrap', buttonClassName)}
            dataTestAttrs={{
              component: 'StorableExport',
              element: 'button',
              key: 'exportDropdownToggle',
            }}
          />
        </Dropdown>
      ) : (
        <ExportAuditLogButton
          buttonStyle={auditExportButtonStyle}
          ids={[storableId]}
          className="u-mini-spacing-left"
        />
      )}
    </>
  );
}
